import {
  defaults,
  defaultsDeep,
  defaultTo,
  each,
  get,
  has,
  keys,
  reduce,
  replace,
  set,
  uniqueId
} from 'lodash'
import Field, { type FieldConfig, type Manifest } from './Field'
import ValidationError, { type Errors } from './Errors/ValidationError'

export enum RequestOperation {
  REQUEST_CONTINUE = 0,
  REQUEST_REDUNDANT = 1,
  REQUEST_SKIP = 2
}

/**
 * Binds all methods of a class instance to itself.
 */
export const autobind = function (instance: any): void {
  for (let obj = instance; obj; obj = Object.getPrototypeOf(obj)) {
    // We're the end of the inheritance chain if we've reached 'Object'.
    if (obj.constructor.name === 'Object') {
      return
    }
    const names: string[] = Object.getOwnPropertyNames(obj)
    // Bind each function to the instance.
    for (let i = 0; i < names.length; i++) {
      let name: string = names[i]
      // No need to bind getters, as attempting to access them would also
      // invoke them which is something we don't want to do here.
      if (Object.getOwnPropertyDescriptor(obj, name)!.get) {
        continue
      }
      // We're using `defineProperty` here so that we don't make all the
      // class methods enumerable when we replace them.
      if (typeof obj[name] === 'function' && name !== 'constructor') {
        Object.defineProperty(instance, name, {
          value: instance[name].bind(instance),
          enumerable: false,
          configurable: true,
          writable: true,
        })
      }
    }
  }
}

/**
 * Base class for all things common between Model and Collections.
 */
abstract class HyBase {

  // Values for request outcomes
  static readonly REQUEST_CONTINUE = RequestOperation.REQUEST_CONTINUE
  static readonly REQUEST_REDUNDANT = RequestOperation.REQUEST_REDUNDANT
  static readonly REQUEST_SKIP = RequestOperation.REQUEST_SKIP

  // Values shared with Models and Collections

  // Activity
  _flags!: Record<string, boolean>
  // Reserved keywords that can't be used for attribute or option names.
  static readonly RESERVED = useInvert([
    '_values',
    'attributes',
    'deleting',
    'errors',
    'fatal',
    'loading',
    'saving'
  ])
  // Internal Properties
  readonly _uid!: string
  private readonly _options!: Record<string, any>
  private readonly _fields!: Record<string, Field>
  // private readonly _listeners!: Record<string, Listener[]>;

  // TODO: Document constructor
  protected constructor(options: Options) {
    autobind(this);
    // Define an automatic unique ID. This is primarily to distinguish
    // between multiple instances of the same name and data.
    Object.defineProperty(this, '_uid', {
      value: uniqueId(),
      enumerable: false,
      configurable: false,
      writable: false
    })
    // Initialize state flags
    this._flags = reactive({
      loading: false,
      saving: false,
      deleting: false,
      fatal: false
    })
    // Set internal attributes
    // TODO: Do we need listeners?
    // Vue.set(this, '_listeners', {});  // Event listeners
    this._options = reactive({})
    this._fields = reactive({})
    // Set internal options
    this.setOptions(options)
    // Load manifest
    this.setFields()
    // Hook for additional functionality
    this.boot()
  }

  /**
   * Called after construction, this hook allows you to add some extra setup
   * logic without having to override the constructor.
   */
  boot(): void { }

  /**
   * @returns {string} The class name of this instance.
   */
  get $class(): string {
    return Object.getPrototypeOf(this).constructor.name
  }

  /**
   * @returns {string} Default string representation.
   */
  toString(): string {
    return `<${this.$class} #${this._uid}>`
  }

  // Inheritance

  abstract get errors(): Record<string, string | string[]>
  abstract reset(attributes: string | string[]): void

  /**
   * Creates a validation error using given errors and an optional message.
   */
  createValidationError(
    errors: Errors | Errors[],
    message?: string
  ): ValidationError {
    return new ValidationError(errors, message)
  }

  // Flags

  /**
   * @returns True when a fetch operation is in progress
   */
  get loading(): boolean {
    return this._flags['loading']
  }

  /**
   * @returns True when a save operation is in progress
   */
  get saving(): boolean {
    return this._flags['saving']
  }

  /**
   * @returns True when a delete operation is in progress
   */
  get deleting(): boolean {
    return this._flags['deleting']
  }

  /**
   * @returns True when a fetch|save|delete operation has failed
   */
  get fatal(): boolean {
    return this._flags['fatal']
  }

  /**
   * @param flag  The name of the flag to set
   * @param value The value of the flag to set
   */
  setFlag(flag: string, value: boolean) {
    this._flags[flag] = value
  }

  /**
   * Resets model state, ie. `loading`, etc back to their initial states.
   */
  clearState(): void {
    this._flags['loading'] = false
    this._flags['saving'] = false
    this._flags['deleting'] = false
    this._flags['fatal'] = false
  }

  // Options

  /**
   * @returns {Object} This instance's default options.
   */
  options(): Options {
    return {}
  }

  /**
   * @returns {Object} This class' default options.
   */
  getDefaultOptions(): Options {
    return {
      // Default identifier is id
      identifier: 'id',
      // Default REST actions (method and route template)
      actions: {
        ...this.getDefaultActions(),
        ...this.actions()
      }
      // The HTTP status code to use for indicating a validation error.
      // TODO: Figure out standard validation error
      // validationErrorStatus: 422,
    }
  }

  /**
   * Returns the requested option
   * 
   * @param {Array|string} path     Option path resolved by `get`
   * @param {*}            fallback Fallback value if the option is not set.
   *
   * @returns {*} The value of the given option path.
   */
  getOption(path: string | string[], fallback: any = null): any {
    return get(this._options, path, fallback)
  }

  /**
   * Returns all the options that are currently set on this instance.
   *
   * @return {Object}
   */
  getOptions(): Options {
    return defaultTo(this._options, {})
  }

  /**
   * Sets an option.
   *
   * @param {string} path
   * @param {*}      value
   */
  setOption(path: string, value: any): void {
    set(this._options, path, value)
  }

  /**
   * Sets all given options. Successive values for the same option won't be
   * overwritten, so this follows the 'defaults' behaviour, and not 'merge'.
   *
   * @param {...Object} options One or more objects of options.
   */
  setOptions(...options: Options[]): void {
    const mergedOptions = defaultsDeep(
      {},
      ...options, // Given options
      this.options(), // Instance defaults
      this.getDefaultOptions() // Class defaults
    )
    // Copy merged options
    each(keys(mergedOptions), (option): void => {
      this._options[option] = mergedOptions[option]
    })
    // Add field for identifier
    this.setField(mergedOptions.identifier)
  }

  // Fields

  // TODO: getDefaultFields() documentation
  getDefaultFields(): Manifest {
    return {}
  }

  // TODO: manifest() documentation
  manifest(): Manifest {
    return {}
  }

  abstract get(field: string): any
  abstract set(field: string, value: any): any

  // TODO: setField() documentation
  setField(field: string, config: FieldConfig = {}) {
    // Protect against unwillingly using an attribute name that already
    // exists as an internal property or method name.
    if (has(HyBase.RESERVED, field)) {
      throw new Error(`Can't use reserved field name '${field}'`)
    }
    // Add field object to fields
    this._fields[field] = Field.make(field, config, this)
    // Create dynamic accessors and mutations so that we can update the
    // model directly while also keeping the model attributes in sync.
    Object.defineProperty(this, field, {
      get: (): any => this.get(field),
      set: <T>(value: T): T | undefined => this.set(field, value)
    })
  }

  // TODO: setFields() documentation
  setFields() {
    const manifest = {
      ...this.getDefaultFields(),
      ...this.manifest()
    }
    each(manifest, (config, field): void => {
      // Protect against unwillingly using an attribute name that already
      // exists as an internal property or method name.
      if (has(HyBase.RESERVED, field)) {
        throw new Error(`Can't use reserved attribute name '${field}'`)
      }
      // Create field
      this.setField(field, config)
    })
  }

  // TODO: hasField() documentation
  hasField(field: string) {
    return this._fields[field] instanceof Field
  }

  // TODO: field() documentation
  field(field: string): Field {
    return this._fields[field]
  }

  // TODO: fields() documentation
  fields(field?: string | undefined): Field | Field[] {
    if (field) {
      return this._fields[field]
    } else {
      return Object.values(this._fields)
    }
  }

  // TODO: fieldNames() documentation
  fieldNames(): string[] {
    return Object.keys(this._fields)
  }

  // Actions

  /**
   * Returns any additional REST actions available to this model.
   *
   * @returns {Object<string, Action>}
   */
  actions(): Actions {
    return {}
  }

  abstract getDefaultActions(): Actions

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {Object<string, Action>} The corresponding action details
   */
  getAction(action: string): Action {
    return this.getOption(`actions.${action}`)
  }

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {string} Base URL of the server to execute the action on
   */
  getBase(_action: Action): HttpMethod {
    const config = useRuntimeConfig()
    return config.public.apiUrl
  }

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {HttpMethod} HTTP method to use when executing the action
   */
  getMethod(action: Action): HttpMethod {
    return action.method
  }

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {Object} Query parameters to use when executing the action
   */
  getQuery(_action: Action): Record<string, string | number> {
    return {}
  }

  /**
   * Set with any additional non-attribute parameters to use
   * for replacement in route patterns.
   *
   * @returns {object}
   */
  getRouteParameters(): Record<string, string> {
    return {}
  }

  /**
   * @param route  The string containing the route
   * 
   * @returns {object} An object consisting of all route string replacements,
   * eg for `'route/{one}/{two}/three'` it will return
   * `{ '{one}': 'value-1', '{two}': 'value-2' }`
   */
  getRouteReplacements(route: string): Record<string, string> {
    // Make a list of parameters that will be replaced
    const replace: Record<string, string> = {}
    let pattern: RegExp = /\{([^}]+)\}/
    pattern = new RegExp(
      pattern instanceof RegExp ? pattern.source : pattern,
      'g'
    )
    // Get additional route parameters and values
    const parameters: Record<string, string> = this.getRouteParameters()
    // Execute replacements
    for (
      let parameter: RegExpExecArray | null;
      (parameter = pattern.exec(route)) !== null;
    ) {
      replace[parameter[0]] = parameters[parameter[1]]
    }
    // Return replacements
    return replace
  }

  /**
   * @param {string} action The name of the action to execute
   *
   * @returns {string} A URL that was generated using the given route key.
   */
  getURL(action: Action): string {
    // Get replacements
    const replacements: Record<string, string> = this.getRouteReplacements(action.route)
    // Replace all route parameters with their replacement values.
    return reduce(
      replacements,
      (result, value, parameter): string => {
        return replace(result, parameter, value)
      },
      action.route
    )
  }

  // Headers

  /**
   * @returns {Object} Headers to use when executing the action
   */
  getHeaders(action: Action): Record<string, string | number> {
    return {}
  }

  // Client

  /**
   * This is the central component for all HTTP requests and handling.
   *
   * @param  {Object}     config      Request configuration
   * @param  {function}   onPreFlight   Called before the request is made.
   * @param  {function}   onSuccess   Called when the request was successful.
   * @param  {function}   onFailure   Called when the request failed.
   */
  request(
    url: string,
    config: Record<string, string | number | object>,
    onPreFlight: PreFlightCallback,
    onSuccess: RequestSuccessCallback,
    onFailure: RequestFailureCallback
  ): Promise<unknown | null> {
    // Disable caching by adding a unique identifier to the URL.
    url = `${url}#${this._uid}`
    // Build promise for returning
    return new Promise((resolve, reject): Promise<void> => {
      // Run preflight
      return onPreFlight()
        .then(async (status: RequestOperation | boolean): Promise<void> => {
          // Determine how to proceed
          switch (status) {
            case RequestOperation.REQUEST_CONTINUE:
              // Continue
              break
            case RequestOperation.REQUEST_SKIP:
              // Skip, nothing else to do
              return
            case RequestOperation.REQUEST_REDUNDANT:
              // Skip, but consider it a success.
              onSuccess(null)
              resolve(null)
              return
          }
          // Execute request
          const response = await useFetch(url, config)
          // Handle response
          if (response.error.value) {
            // Error
            onFailure(response.error.value)
            reject(response.error.value)
          } else {
            // Success
            onSuccess(response)
            resolve(response.data.value)
          }
        })
      // Catch leftover errors
      // .catch(reject)
    })
  }

  // Fetching

  abstract onFetch(): Promise<RequestOperation>
  abstract onFetchSuccess(data: any | null): void
  abstract onFetchFailure(error: any): void

  /**
   * Fetches data from the API.
   *
   * @param {options}             Fetch options
   * @param {options.method}      Fetch HTTP method
   * @param {options.url}         Fetch URL
   * @param {options.params}      Query params
   * @param {options.headers}     Query headers
   *
   * @returns {Promise}
   */
  async fetch(options: RequestOptions = {}): Promise<any | null> {
    // Get action
    const action = this.getAction(defaultTo(options.action, 'fetch'))
    // Build URL
    const url = defaultTo(
      options.url,
      `${this.getBase(action)}/${this.getURL(action)}`
    )
    // Build configuration
    const config = {
      method: defaultTo(options.method, this.getMethod(action)),
      params: defaults(options.params, this.getQuery(action)),
      headers: {
        ...defaults(options.headers, this.getHeaders(action))
      },
      credentials: 'include'
    }
    // Make request and return promise
    return this.request(
      url,
      config,
      this.onFetch,
      this.onFetchSuccess,
      this.onFetchFailure
    )
    // .then((response) => {console.log('good request', response)})
    // .catch((error) => {console.log('bad request', error)})
    // .finally(() => {console.log('finally request')})
  }

  // Saving

  abstract getSaveData(formData: FormData): void
  abstract onSave(): Promise<RequestOperation>
  abstract onSaveSuccess(data: any | null): void
  abstract onSaveFailure(error: any): void

  /**
   * Persists data to the database/API.
   *
   * @param {options}             Save options
   * @param {options.method}      Save HTTP method
   * @param {options.url}         Save URL
   * @param {options.data}        Save data
   * @param {options.params}      Query params
   * @param {options.headers}     Query headers
   *
   * @returns {Promise}
   */
  save(options: RequestOptions = {}): Promise<any | null> {
    // Get action
    const action = this.getAction(defaultTo(options.action, 'save'))
    // Build URL
    const url = defaultTo(
      options.url,
      `${this.getBase(action)}/${this.getURL(action)}`
    )
    // Build configuration
    const config = {
      method: defaultTo(options.method, this.getMethod(action)),
      params: defaults(options.params, this.getQuery(action)),
      headers: {
        ...defaults(options.headers, this.getHeaders(action))
      },
      credentials: 'include',
      body: new FormData()
    }
    // Build payload
    this.getSaveData(config.body)
    // Make request and return promise
    return this.request(
      url,
      config,
      this.onSave,
      this.onSaveSuccess,
      this.onSaveFailure
    )
  }

  // Deleting

  abstract onDelete(): Promise<RequestOperation>
  abstract onDeleteSuccess(data: any | null): void
  abstract onDeleteFailure(error: any): void

  /**
   * Removes model or collection data from the database/API.
   *
   * @param {options}             Delete options
   * @param {options.method}      Delete HTTP method
   * @param {options.url}         Delete URL
   * @param {options.params}      Query params
   * @param {options.headers}     Query headers
   *
   * @returns {Promise}
   */
  delete(options: RequestOptions = {}): Promise<any | null> {
    // Get action
    const action = this.getAction(defaultTo(options.action, 'delete'))
    // Build URL
    const url = defaultTo(
      options.url,
      `${this.getBase(action)}/${this.getURL(action)}`
    )
    // Build configuration
    const config = {
      method: defaultTo(options.method, this.getMethod(action)),
      params: defaults(options.params, this.getQuery(action)),
      headers: {
        ...defaults(options.headers, this.getHeaders(action))
      },
      credentials: 'include',
    }
    // Make request and return promise
    return this.request(
      url,
      config,
      this.onDelete,
      this.onDeleteSuccess,
      this.onDeleteFailure
    )
  }
}

export default HyBase

// export type Listener = (context: Record<string, any>) => void;
export type RouteResolver = (
  route: string,
  parameters: Record<string, string>
) => string
export type PreFlightCallback = () => Promise<number | boolean>
export type RequestFailureCallback = (error: any) => void
export type RequestSuccessCallback = (response: unknown | null) => void

// Actions
export type ActionType = 'fetch' | 'save' | 'delete' | string
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' | string
export type Action = { method: HttpMethod; route: string }
export type Actions = Record<ActionType, Action>

export interface Options {
  [key: string]: any
  actions?: Actions
  // validationErrorStatus?: number;
  // useDeleteBody?: boolean;
}

export interface RequestOptions {
  url?: string
  baseUrl?: string
  method?: HttpMethod
  action?: ActionType
  data?: any
  params?: Record<string, any>
  headers?: Record<string, any>
}
